import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'

const rememberStyles = (element: HTMLElement, properties: string[]) => {
  const previous = properties.map(property => ({
    property,
    value: element.style.getPropertyValue(property),
    priority: element.style.getPropertyPriority(property),
  }))
  return () => previous.forEach(({ property, value, priority }) => {
    if (value) element.style.setProperty(property, value, priority)
    else element.style.removeProperty(property)
  })
}

/** Fix the document in place on iOS too; only the dialog's result area may scroll. */
export const lockSearchPageScroll = (dialog: HTMLElement | (() => HTMLElement | null)) => {
  const getDialog = typeof dialog === 'function' ? dialog : () => dialog
  const doc = getDialog()?.ownerDocument || document
  const win = doc.defaultView!
  const { body, documentElement: html } = doc
  const bodyStyle = win.getComputedStyle(body)
  const alreadyFixed = bodyStyle.position === 'fixed'
  const fixedOffset = (value: string, fallback: number) => Number.isFinite(parseFloat(value)) ? -parseFloat(value) : fallback
  const x = alreadyFixed ? fixedOffset(bodyStyle.left, win.scrollX) : win.scrollX
  const y = alreadyFixed ? fixedOffset(bodyStyle.top, win.scrollY) : win.scrollY
  const scrollbar = Math.max(0, win.innerWidth - html.clientWidth)
  const stableGutter = win.getComputedStyle(html).getPropertyValue('scrollbar-gutter').includes('stable')
  const padding = (parseFloat(bodyStyle.paddingRight) || 0) + (!alreadyFixed && !stableGutter ? scrollbar : 0)
  // Own only this temporary rule, not body inline styles that another dialog
  // (e.g. cookie consent) can restore while search remains open.
  const lockStyle = doc.createElement('style')
  const nonce = doc.querySelector<HTMLStyleElement>('style[nonce]')?.nonce
  if (nonce) lockStyle.nonce = nonce
  lockStyle.dataset.searchScrollLock = ''
  lockStyle.textContent = `
    html { overflow: hidden !important; overscroll-behavior: none !important; }
    body { position: fixed !important; top: ${-y}px !important; left: ${-x}px !important;
      right: auto !important; width: ${alreadyFixed ? bodyStyle.width || '100%' : '100%'} !important;
      overflow: hidden !important; padding-right: ${padding}px !important; }
  `
  doc.head.appendChild(lockStyle)

  const canScrollResults = (target: EventTarget | null, deltaY: number) => {
    const area = getDialog()?.querySelector<HTMLElement>('[data-search-scroll-area]')
    if (!area || !(target instanceof win.Node) || !area.contains(target)) return false
    const maximum = area.scrollHeight - area.clientHeight
    if (maximum <= 1) return false
    return deltaY < 0 ? area.scrollTop > 0 : area.scrollTop < maximum - 1
  }
  const stopOutsideResults = (event: Event, deltaX: number, deltaY: number) => {
    // Keep horizontal text selection/caret gestures and browser zoom available.
    if (!event.cancelable || Math.abs(deltaY) <= Math.abs(deltaX)) return
    if (!canScrollResults(event.target, deltaY)) event.preventDefault()
  }
  let touch: { x: number; y: number } | undefined
  const onTouchStart = (event: TouchEvent) => {
    touch = event.touches.length === 1
      ? { x: event.touches[0].clientX, y: event.touches[0].clientY }
      : undefined
  }
  const onTouchMove = (event: TouchEvent) => {
    if (event.touches.length !== 1) { touch = undefined; return }
    const current = event.touches[0]
    if (touch) stopOutsideResults(event, touch.x - current.clientX, touch.y - current.clientY)
    touch = { x: current.clientX, y: current.clientY }
  }
  const onWheel = (event: WheelEvent) => {
    if (!event.ctrlKey) stopOutsideResults(event, event.deltaX, event.deltaY)
  }
  doc.addEventListener('touchstart', onTouchStart, { passive: true, capture: true })
  doc.addEventListener('touchmove', onTouchMove, { passive: false, capture: true })
  doc.addEventListener('touchend', onTouchStart, { passive: true, capture: true })
  doc.addEventListener('touchcancel', onTouchStart, { passive: true, capture: true })
  doc.addEventListener('wheel', onWheel, { passive: false, capture: true })

  let released = false
  return () => {
    if (released) return
    released = true
    doc.removeEventListener('touchstart', onTouchStart, true)
    doc.removeEventListener('touchmove', onTouchMove, true)
    doc.removeEventListener('touchend', onTouchStart, true)
    doc.removeEventListener('touchcancel', onTouchStart, true)
    doc.removeEventListener('wheel', onWheel, true)
    lockStyle.remove()
    if (win.getComputedStyle(body).position !== 'fixed') {
      // Restore synchronously before Next navigation reads the outgoing scroll
      // position. Never let smooth scrolling or a delayed callback affect it.
      const restoreBehavior = rememberStyles(html, ['scroll-behavior'])
      html.style.setProperty('scroll-behavior', 'auto', 'important')
      win.scrollTo(x, y)
      restoreBehavior()
    }
  }
}

const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

export default function useSearchScrollLock(open: boolean, dialog: HTMLElement | null) {
  const release = useRef<(() => void) | undefined>()
  const dialogRef = useRef(dialog)
  dialogRef.current = dialog
  useBrowserLayoutEffect(() => {
    if (!open) return
    // Lock before paint even when MUI creates its portal on a later render.
    const unlock = lockSearchPageScroll(() => dialogRef.current)
    release.current = unlock
    return () => {
      unlock()
      if (release.current === unlock) release.current = undefined
    }
  }, [open])
  return useCallback(() => { release.current?.() }, [])
}
